import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { adminButton } from '@/components/admin/ui';
import { useQuoteVatSettings, useSaveQuoteVatSettings } from '@/hooks/useQuotes';

const vatSchema = z.object({
  vat_registered: z.boolean(),
  vat_rate: z.coerce
    .number({ invalid_type_error: 'Enter a rate' })
    .min(0, 'Cannot be negative')
    .max(100, 'Must be 100 or less'),
  vat_number: z.string().trim().max(30, 'That looks too long'),
});

type VatValues = z.infer<typeof vatSchema>;

/**
 * Org-wide VAT settings. Many small cleaners aren't VAT registered, so this is
 * off by default and prices are shown as entered. When switched on, new quotes
 * add VAT at the rate here and show the VAT number.
 */
export function VatSettingsDialog({
  orgId,
  open,
  onOpenChange,
  canWrite,
}: {
  orgId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canWrite: boolean;
}) {
  const { data: settings } = useQuoteVatSettings(orgId);
  const save = useSaveQuoteVatSettings();
  const form = useForm<VatValues>({
    resolver: zodResolver(vatSchema),
    defaultValues: { vat_registered: false, vat_rate: 20, vat_number: '' },
  });

  useEffect(() => {
    if (!open || !settings) return;
    form.reset({
      vat_registered: settings.vat_registered,
      vat_rate: settings.vat_rate,
      vat_number: settings.vat_number,
    });
  }, [open, settings, form]);

  const registered = form.watch('vat_registered');

  const onSubmit = async (values: VatValues) => {
    if (!orgId || !settings?.companyInfoId) {
      toast.error('Add your company details first, then set up VAT.');
      return;
    }
    try {
      await save.mutateAsync({
        orgId,
        companyInfoId: settings.companyInfoId,
        vat_registered: Boolean(values.vat_registered),
        vat_rate: Number(values.vat_rate) || 0,
        vat_number: values.vat_number ?? '',
      });
      toast.success('VAT settings saved');
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save VAT settings');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-2xl p-5 sm:p-6">
        <DialogHeader className="space-y-1">
          <DialogTitle className="text-[17px] font-semibold tracking-tight">VAT settings</DialogTitle>
          <DialogDescription>
            Not VAT registered? Leave this off and your prices are shown exactly as you enter them.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="adm-form space-y-4">
            <FormField
              control={form.control}
              name="vat_registered"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between gap-3 rounded-xl border border-border bg-muted/40 p-3">
                  <div>
                    <FormLabel htmlFor="vat-registered">I'm VAT registered</FormLabel>
                    <FormDescription className="text-[12px]">Adds a VAT line to new quotes.</FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      id="vat-registered"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={!canWrite}
                      aria-label="VAT registered"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            {registered && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-[120px_1fr]">
                <FormField
                  control={form.control}
                  name="vat_rate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>VAT rate (%)</FormLabel>
                      <FormControl>
                        <Input type="number" step="0.01" min="0" max="100" disabled={!canWrite} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="vat_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>VAT number</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. GB123456789" disabled={!canWrite} {...field} />
                      </FormControl>
                      <FormDescription className="text-[12px]">Shown on your quotes and invoices.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}
            <DialogFooter className="gap-2 sm:gap-2">
              <Button type="button" variant="ghost" size="sm" className={adminButton.outline} onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" className={adminButton.primary} disabled={!canWrite || save.isPending}>
                {save.isPending && <Loader2 className="animate-spin" />}
                Save
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
